// ============================================================================
// HEMATOLOGY CONCEPTS (Phase L-3 — lab-heavy specialty branch)
//
// specialtyId = 'hematology'. The hematology branch: CBC-derived labs, iron /
// B12-folate / hemolysis / coagulation, cytopenia/cytosis findings, key lab
// documents, and a conservative red-flag set. This is NOT a hematology ontology
// — it is a ReferralGate operational evidence library for referral intake.
//
// CLASSIFICATION (L-3 contract — docs/L-3-hematology-concept-library-contract.md):
//   • LIVE_LINKABLE: 7 existing (kept) + 24 new ride the EXISTING LAB / FINDING /
//       RED_FLAG groups → ZERO evidenceAutoLink/matcher logic change. Patterns
//       live in `recognitionPatterns` below; ordered group membership is in
//       index.js. Labs are the safest LIVE type (matcher `nearby_value` requires
//       a numeric value within 80 chars or emits nothing).
//   • DATA_ONLY (35): present + governed, NO pattern, NOT in any group → inert.
//   • DELAYED_RISKY (not added): broad terms (bleeding/bruising/fatigue/infection/
//       clot/low_count/high_count), abnormal_cbc (→ reuse shared abnormal_lab),
//       and all redirect/exclusion concepts (no REDIRECT target type).
//
// TWO PRECEDENCE FIXES (index.js ordered arrays — data, not logic):
//   1. Anemia subtypes (iron_deficiency_anemia, macrocytic_anemia) are ordered
//      BEFORE generic `anemia` in FINDING (else /an[ae]mia/ shadows them).
//   2. `leukopenia` is TIGHTENED here (drop the `neutropenia` alias + pattern) so
//      the dedicated `neutropenia` finding binds.
//
// RED-FLAG PROJECTION GAP (carried from L-2): only positive/negative/uncertainty
// are projected into Pack maps (NOT excluded/commonFalseMatches). So every
// suppression-critical phrase is placed in negative/uncertainty here; excluded/
// commonFalseMatches remain governance-only. L-3 does NOT change the projection.
//
// PURE / Never-PHI. Concept records are pure JSON data; recognition patterns are
// a separate code-level RegExp table (flags 'i' only → stateless).
// ============================================================================

import { makeConcept, CONCEPT_TYPES } from './conceptBuilder.js';

export const concepts = [
  // ══════════════════════════════════════════════════════════════════════════
  // EXISTING LIVE — measurements (LAB group). Moved verbatim.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'white_blood_cell_count', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'hematology',
    canonicalLabel: 'White blood cell count',
    description: 'White blood cell / leukocyte count from a CBC or lab result.',
    aliases: ['WBC', 'WCC', 'white blood cell count', 'white cell count', 'leukocyte count'],
    abbreviations: ['WBC', 'WCC'],
    preferredUnit: 'x10^9/L', allowedUnits: ['x10^9/L', 'K/uL'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'], weakDocumentTypes: ['DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'platelet_count', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'hematology',
    canonicalLabel: 'Platelet count',
    description: 'Platelet count from a CBC or lab result.',
    aliases: ['platelet count', 'platelets', 'PLT'],
    abbreviations: ['PLT'],
    preferredUnit: 'x10^9/L', allowedUnits: ['x10^9/L', 'K/uL'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'hemoglobin', conceptType: CONCEPT_TYPES.MEASUREMENT,
    specialtyId: 'hematology',
    canonicalLabel: 'Hemoglobin',
    description: 'Hemoglobin concentration from a CBC or lab result.',
    aliases: ['hemoglobin', 'haemoglobin', 'Hb', 'Hgb'],
    abbreviations: ['Hb', 'Hgb'],
    preferredUnit: 'g/dL', allowedUnits: ['g/dL', 'g/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // NEW LIVE — measurements (LAB group; number / nearby_value / units)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'ferritin', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Ferritin', description: 'Serum ferritin (iron stores).',
    aliases: ['ferritin', 'serum ferritin'],
    preferredUnit: 'ng/mL', allowedUnits: ['ng/mL', 'ug/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'mean_corpuscular_volume', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Mean corpuscular volume', description: 'MCV — red cell size from a CBC.',
    aliases: ['mean corpuscular volume', 'MCV'], abbreviations: ['MCV'],
    preferredUnit: 'fL', allowedUnits: ['fL'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'vitamin_b12', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Vitamin B12', description: 'Serum vitamin B12 / cobalamin.',
    aliases: ['vitamin B12', 'B12', 'cobalamin'], abbreviations: ['B12'],
    preferredUnit: 'pg/mL', allowedUnits: ['pg/mL', 'pmol/L', 'ng/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'folate', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Folate', description: 'Serum / red cell folate.',
    aliases: ['folate', 'folic acid', 'serum folate'],
    preferredUnit: 'ng/mL', allowedUnits: ['ng/mL', 'nmol/L', 'ug/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'reticulocyte_count', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Reticulocyte count', description: 'Reticulocyte count / percentage.',
    aliases: ['reticulocyte count', 'reticulocytes', 'retic count'],
    preferredUnit: 'x10^9/L', allowedUnits: ['%', 'x10^9/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'lactate_dehydrogenase', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Lactate dehydrogenase', description: 'LDH — hemolysis / turnover marker.',
    aliases: ['lactate dehydrogenase', 'LDH'], abbreviations: ['LDH'],
    preferredUnit: 'U/L', allowedUnits: ['U/L', 'IU/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'haptoglobin', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Haptoglobin', description: 'Haptoglobin — hemolysis marker.',
    aliases: ['haptoglobin'],
    preferredUnit: 'mg/dL', allowedUnits: ['mg/dL', 'g/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'absolute_neutrophil_count', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Absolute neutrophil count', description: 'ANC — neutrophil count from a CBC differential.',
    aliases: ['absolute neutrophil count', 'ANC', 'neutrophil count'], abbreviations: ['ANC'],
    preferredUnit: 'x10^9/L', allowedUnits: ['x10^9/L', 'K/uL'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'lymphocyte_count', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Lymphocyte count', description: 'Absolute lymphocyte count from a CBC differential.',
    aliases: ['lymphocyte count', 'absolute lymphocyte count'],
    preferredUnit: 'x10^9/L', allowedUnits: ['x10^9/L', 'K/uL'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // EXISTING LIVE — findings (FINDING group). `leukopenia` TIGHTENED (neutropenia
  // removed so the dedicated `neutropenia` concept below binds).
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'leukopenia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Leukopenia', description: 'Low white blood cell count finding.',
    aliases: ['leukopenia', 'low WBC', 'low white count'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal WBC', 'no leukopenia', 'normal white count'], uncertaintyPhrases: ['possible leukopenia'],
  }),
  makeConcept({
    conceptId: 'leukocytosis', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Leukocytosis', description: 'Elevated white blood cell count finding.',
    aliases: ['leukocytosis', 'high WBC', 'elevated white count'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal WBC', 'no leukocytosis'], uncertaintyPhrases: ['possible leukocytosis'],
  }),
  makeConcept({
    conceptId: 'thrombocytopenia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Thrombocytopenia', description: 'Low platelet count finding.',
    aliases: ['thrombocytopenia', 'low platelets', 'low platelet count'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal platelets', 'no thrombocytopenia'], uncertaintyPhrases: ['possible thrombocytopenia'],
  }),
  makeConcept({
    conceptId: 'anemia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Anemia', description: 'Low hemoglobin finding (generic).',
    aliases: ['anemia', 'anaemia', 'low hemoglobin'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal hemoglobin', 'no anemia', 'not anemic'], uncertaintyPhrases: ['possible anemia'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // NEW LIVE — findings (FINDING group). Anemia subtypes ordered BEFORE generic
  // `anemia` in index.js (see RECOGNITION_GROUPS).
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'iron_deficiency_anemia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Iron deficiency anemia', description: 'Iron deficiency anemia finding.',
    aliases: ['iron deficiency anemia', 'iron deficiency anaemia', 'IDA', 'iron deficiency'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['no iron deficiency', 'iron stores normal'],
  }),
  makeConcept({
    conceptId: 'macrocytic_anemia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Macrocytic anemia', description: 'Macrocytic anemia / macrocytosis finding.',
    aliases: ['macrocytic anemia', 'macrocytic anaemia', 'macrocytosis'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal MCV', 'no macrocytosis'],
  }),
  makeConcept({
    conceptId: 'neutropenia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Neutropenia', description: 'Low neutrophil count finding.',
    aliases: ['neutropenia', 'low neutrophils', 'low neutrophil count'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal neutrophils', 'no neutropenia'],
  }),
  makeConcept({
    conceptId: 'thrombocytosis', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Thrombocytosis', description: 'Elevated platelet count finding.',
    aliases: ['thrombocytosis', 'high platelets', 'elevated platelets', 'high platelet count'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal platelets', 'no thrombocytosis'],
  }),
  makeConcept({
    conceptId: 'pancytopenia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Pancytopenia', description: 'Reduction in all three blood cell lines.',
    aliases: ['pancytopenia'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['no pancytopenia', 'counts normal'],
  }),
  makeConcept({
    conceptId: 'polycythemia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Polycythemia', description: 'Elevated red cell mass / erythrocytosis.',
    aliases: ['polycythemia', 'polycythaemia', 'erythrocytosis'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal hemoglobin', 'no polycythemia'],
  }),
  makeConcept({
    conceptId: 'lymphocytosis', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Lymphocytosis', description: 'Elevated lymphocyte count finding.',
    aliases: ['lymphocytosis', 'high lymphocytes'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal lymphocytes', 'no lymphocytosis'],
  }),
  makeConcept({
    conceptId: 'lymphadenopathy', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Lymphadenopathy', description: 'Enlarged lymph nodes finding.',
    aliases: ['lymphadenopathy', 'enlarged lymph nodes', 'swollen glands', 'swollen lymph nodes'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'CONSULT_NOTE'],
    negativePhrases: ['no lymphadenopathy', 'no enlarged nodes'],
  }),
  makeConcept({
    conceptId: 'splenomegaly', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Splenomegaly', description: 'Enlarged spleen finding.',
    aliases: ['splenomegaly', 'enlarged spleen'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'IMAGING_REPORT'],
    negativePhrases: ['no splenomegaly', 'spleen not enlarged'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // NEW LIVE — documents (LAB group → LAB_RESULT; CBC precedent). No enum change.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'blood_film', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Blood film', description: 'Peripheral blood film / smear (LAB_RESULT).',
    aliases: ['blood film', 'peripheral smear', 'peripheral blood film', 'blood smear'],
    preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'iron_studies', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Iron studies', description: 'Iron studies / panel (LAB_RESULT).',
    aliases: ['iron studies', 'iron panel', 'iron profile'],
    preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'coagulation_studies', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Coagulation studies', description: 'Coagulation / clotting screen (LAB_RESULT).',
    aliases: ['coagulation studies', 'coagulation screen', 'clotting screen', 'coag screen'],
    preferredDocumentTypes: ['LAB_RESULT'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // NEW LIVE — red flags (RED_FLAG group, positive_only). Suppression-critical
  // phrases live in negative/uncertainty (projection gap — Part D).
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'febrile_neutropenia', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Febrile neutropenia', description: 'Fever in a neutropenic patient — oncologic emergency.',
    positivePhrases: ['febrile neutropenia', 'fever with neutropenia', 'neutropenic fever', 'neutropenic sepsis'],
    negativePhrases: ['no fever', 'afebrile', 'no neutropenia', 'neutrophil count normal', 'normal neutrophils'],
    uncertaintyPhrases: ['possible neutropenic fever', 'suspected neutropenia', 'history of neutropenia', 'resolved neutropenia', 'fever but neutrophils not checked'],
    excludedPhrases: ['remote neutropenia history', 'fever unrelated to neutropenia'],
    commonFalseMatches: ['prophylactic antibiotics'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'suspected_acute_leukemia', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Suspected acute leukemia', description: 'Suspected acute leukemia (blasts / circulating blasts).',
    positivePhrases: ['blasts on blood film', 'circulating blasts', 'suspected acute leukemia', 'acute leukemia suspected'],
    negativePhrases: ['no blasts seen', 'leukemia ruled out', 'no evidence of acute leukemia', 'no circulating blasts'],
    uncertaintyPhrases: ['possible blasts', 'atypical cells noted', 'leukemia query', 'history of leukemia', 'chronic leukemia history', 'family history of leukemia'],
    excludedPhrases: ['chronic leukemia history only'],
    commonFalseMatches: ['leukemia screening', 'leukemia follow-up'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'critically_low_platelet_count', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Critically low platelet count', description: 'Critically low platelets (e.g. <20) — bleeding risk.',
    positivePhrases: ['platelet count below 20', 'platelets below 20', 'platelets <20', 'severe thrombocytopenia with bleeding', 'critically low platelets'],
    negativePhrases: ['platelets normal', 'platelet count stable', 'no severe thrombocytopenia', 'normal platelet count'],
    uncertaintyPhrases: ['possible low platelets', 'platelet clumping suspected', 'repeat CBC pending', 'pseudothrombocytopenia suspected', 'historical low platelet count'],
    excludedPhrases: ['historical low platelet count resolved'],
    commonFalseMatches: ['mild thrombocytopenia', 'stable low platelets'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — measurements (no pattern, no group → inert). Coag labs are
  // cross-specialty VTE; never use \bpt\b (matches "patient").
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'inr', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'INR', description: 'International normalized ratio (coagulation).',
    aliases: ['INR', 'international normalized ratio'], abbreviations: ['INR'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'prothrombin_time', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Prothrombin time', description: 'Prothrombin time (coagulation).',
    aliases: ['prothrombin time'],
    preferredUnit: 'seconds', allowedUnits: ['seconds', 'sec', 's'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'activated_partial_thromboplastin_time', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Activated partial thromboplastin time', description: 'APTT (coagulation).',
    aliases: ['activated partial thromboplastin time', 'APTT', 'PTT'], abbreviations: ['APTT', 'PTT'],
    preferredUnit: 'seconds', allowedUnits: ['seconds', 'sec', 's'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'd_dimer', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'D-dimer', description: 'D-dimer (thrombosis marker).',
    aliases: ['d-dimer', 'd dimer'],
    preferredUnit: 'ng/mL', allowedUnits: ['ng/mL', 'mg/L', 'ug/mL'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'iron_level', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Serum iron', description: 'Serum iron level.',
    aliases: ['serum iron', 'iron level'],
    preferredUnit: 'ug/dL', allowedUnits: ['ug/dL', 'umol/L'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'transferrin_saturation', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Transferrin saturation', description: 'Transferrin saturation (TSAT).',
    aliases: ['transferrin saturation', 'TSAT'], abbreviations: ['TSAT'],
    preferredUnit: '%', allowedUnits: ['%'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'bilirubin', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hematology',
    canonicalLabel: 'Bilirubin', description: 'Total bilirubin (hemolysis / hepatic — cross-specialty).',
    aliases: ['bilirubin', 'total bilirubin'],
    preferredUnit: 'mg/dL', allowedUnits: ['mg/dL', 'umol/L'],
    requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — findings
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'hemolytic_anemia', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Hemolytic anemia', description: 'Hemolytic anemia finding.',
    aliases: ['hemolytic anemia', 'haemolytic anaemia', 'hemolysis'], preferredDocumentTypes: ['LAB_RESULT'],
    negativePhrases: ['no hemolysis', 'no evidence of hemolysis'],
  }),
  makeConcept({
    conceptId: 'monoclonal_gammopathy', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Monoclonal gammopathy', description: 'Paraprotein / monoclonal gammopathy finding.',
    aliases: ['monoclonal gammopathy', 'paraprotein', 'MGUS', 'M protein'], abbreviations: ['MGUS'],
    preferredDocumentTypes: ['LAB_RESULT', 'CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'suspected_lymphoma', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Suspected lymphoma', description: 'Suspected lymphoma referral concern.',
    aliases: ['suspected lymphoma', 'lymphoma'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'bleeding_disorder', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Bleeding disorder', description: 'Bleeding disorder / coagulopathy concern.',
    aliases: ['bleeding disorder', 'coagulopathy', 'hemophilia'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'clotting_disorder', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Clotting disorder', description: 'Thrombophilia / hypercoagulable concern.',
    aliases: ['clotting disorder', 'thrombophilia', 'hypercoagulable'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'venous_thromboembolism', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hematology',
    canonicalLabel: 'Venous thromboembolism', description: 'VTE (DVT / PE) concern — cross-specialty.',
    aliases: ['venous thromboembolism', 'VTE', 'DVT', 'pulmonary embolism', 'PE'], abbreviations: ['VTE', 'DVT', 'PE'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'DISCHARGE_SUMMARY', 'IMAGING_REPORT'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — documents (no enum expansion)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'hemolysis_screen', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Hemolysis screen', description: 'Hemolysis screen (LAB_RESULT).',
    aliases: ['hemolysis screen', 'haemolysis screen'], preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'b12_folate_results', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'B12 and folate results', description: 'B12 / folate results (LAB_RESULT).',
    aliases: ['B12 and folate', 'b12 folate results', 'vitamin levels'], preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'bone_marrow_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Bone marrow report', description: 'Bone marrow biopsy / aspirate report (maps to OTHER).',
    aliases: ['bone marrow report', 'bone marrow biopsy', 'bone marrow aspirate'],
    preferredDocumentTypes: ['OTHER'], acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'flow_cytometry_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hematology',
    canonicalLabel: 'Flow cytometry report', description: 'Flow cytometry / immunophenotyping (maps to OTHER).',
    aliases: ['flow cytometry', 'flow cytometry report', 'immunophenotyping'],
    preferredDocumentTypes: ['OTHER'], acceptableDocumentTypes: ['LAB_RESULT'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — symptoms
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'petechiae', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Petechiae', description: 'Petechiae sign.', aliases: ['petechiae', 'petechial rash'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'purpura', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Purpura', description: 'Purpura sign.', aliases: ['purpura', 'purpuric rash'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'easy_bruising', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Easy bruising', description: 'Easy / spontaneous bruising symptom.', aliases: ['easy bruising', 'easily bruised', 'spontaneous bruising'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'epistaxis', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Epistaxis', description: 'Nosebleed symptom.', aliases: ['epistaxis', 'nosebleed', 'nose bleed'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'gum_bleeding', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Gum bleeding', description: 'Bleeding gums symptom.', aliases: ['gum bleeding', 'bleeding gums'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'recurrent_infections', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Recurrent infections', description: 'Recurrent / frequent infections symptom.', aliases: ['recurrent infections', 'frequent infections'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'bone_pain', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hematology', canonicalLabel: 'Bone pain', description: 'Bone / skeletal pain symptom.', aliases: ['bone pain', 'skeletal pain'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  // heavy_menstrual_bleeding MOVED to obstetrics_gynecology (LIVE) in L-16 — no longer here.

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — medication / exposure
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'heparin_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE, specialtyId: 'hematology', canonicalLabel: 'Heparin exposure', description: 'Heparin / LMWH exposure (HIT concern).', aliases: ['heparin', 'low molecular weight heparin', 'LMWH', 'enoxaparin'], preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'chemotherapy_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE, specialtyId: 'hematology', canonicalLabel: 'Chemotherapy exposure', description: 'Cytotoxic chemotherapy exposure.', aliases: ['chemotherapy', 'chemo', 'cytotoxic'], preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE', 'CONSULT_NOTE'] }),
  makeConcept({ conceptId: 'medication_induced_cytopenia', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE, specialtyId: 'hematology', canonicalLabel: 'Medication-induced cytopenia', description: 'Drug-induced cytopenia concern.', aliases: ['drug-induced cytopenia', 'medication-induced cytopenia', 'drug induced neutropenia'], preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'alcohol_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE, specialtyId: 'hematology', canonicalLabel: 'Alcohol exposure', description: 'Heavy alcohol use (macrocytosis / marrow suppression).', aliases: ['alcohol use', 'heavy alcohol', 'alcohol excess'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — red flags (fully boundaried, NOT recognition-reachable)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'blasts_on_blood_film', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Blasts on blood film', description: 'Circulating blasts on the blood film.',
    positivePhrases: ['blasts on blood film', 'circulating blasts'],
    negativePhrases: ['no blasts seen', 'no circulating blasts'],
    uncertaintyPhrases: ['possible blasts', 'atypical cells'],
    excludedPhrases: ['reactive lymphocytes'], commonFalseMatches: ['blast crisis history'],
    preferredDocumentTypes: ['LAB_RESULT', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'suspected_ttp', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Suspected TTP', description: 'Suspected thrombotic thrombocytopenic purpura.',
    positivePhrases: ['suspected TTP', 'thrombotic thrombocytopenic purpura', 'MAHA with thrombocytopenia'],
    negativePhrases: ['no schistocytes', 'TTP excluded'],
    uncertaintyPhrases: ['possible TTP', 'query TTP'],
    excludedPhrases: ['ITP not TTP'], commonFalseMatches: ['TTP protocol'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'LAB_RESULT', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'suspected_dvt_pe', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Suspected DVT / PE', description: 'Suspected venous thromboembolism (DVT / PE).',
    positivePhrases: ['suspected DVT', 'suspected pulmonary embolism', 'suspected PE', 'acute venous thromboembolism'],
    negativePhrases: ['DVT excluded', 'PE ruled out', 'no DVT'],
    uncertaintyPhrases: ['possible DVT', 'query PE', 'old DVT', 'prior PE resolved'],
    excludedPhrases: ['remote DVT history'], commonFalseMatches: ['DVT prophylaxis'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'IMAGING_REPORT'],
  }),
  makeConcept({
    conceptId: 'severe_anemia_symptoms', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Severe symptomatic anemia', description: 'Severe / symptomatic anemia (chest pain, syncope).',
    positivePhrases: ['severe symptomatic anemia', 'symptomatic anemia', 'anemia with chest pain', 'anemia with syncope'],
    negativePhrases: ['mild anemia', 'asymptomatic anemia', 'no anemia symptoms'],
    uncertaintyPhrases: ['possible symptomatic anemia'],
    excludedPhrases: ['chronic stable anemia'], commonFalseMatches: ['anemia of chronic disease'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'rapidly_progressive_cytopenia', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Rapidly progressive cytopenia', description: 'Rapidly falling blood counts.',
    positivePhrases: ['rapidly progressive cytopenia', 'rapidly falling counts', 'rapidly dropping counts'],
    negativePhrases: ['stable counts', 'counts stable'],
    uncertaintyPhrases: ['possible falling counts'],
    excludedPhrases: ['chronic stable cytopenia'], commonFalseMatches: ['mild cytopenia'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'LAB_RESULT', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'pancytopenia_with_systemic_symptoms', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hematology',
    canonicalLabel: 'Pancytopenia with systemic symptoms', description: 'Pancytopenia plus fever / weight loss / systemic features.',
    positivePhrases: ['pancytopenia with systemic symptoms', 'pancytopenia with fever', 'pancytopenia with weight loss'],
    negativePhrases: ['isolated pancytopenia', 'no systemic symptoms'],
    uncertaintyPhrases: ['possible systemic involvement'],
    excludedPhrases: ['chronic pancytopenia'], commonFalseMatches: ['mild pancytopenia'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
];

// ============================================================================
// DELAYED_RISKY — intentionally NOT added in L-3 (documented for governance).
// Broad terms (bleeding/bruising/fatigue/infection/clot/low_count/high_count) get
// no match-time negation for findings → unsafe. `abnormal_cbc` → reuse shared
// `abnormal_lab` (no fork). Redirect/exclusion concepts have no REDIRECT target.
// ============================================================================
export const DELAYED_RISKY_HEMATOLOGY_CONCEPT_IDS = Object.freeze([
  'bleeding', 'bruising', 'fatigue', 'infection', 'clot', 'low_count', 'high_count', 'abnormal_cbc',
  'emergency_bleeding', 'emergency_sepsis', 'surgical_bleeding_source',
  'gynecology_primary_heavy_menstrual_bleeding', 'gastroenterology_primary_gi_bleeding',
  'oncology_primary_solid_tumor', 'nephrology_primary_renal_anemia',
]);

// ============================================================================
// HEMATOLOGY RECOGNITION PATTERNS (code-level RegExp, keyed by conceptId).
// EXACTLY the LIVE concepts (7 existing + 24 new). `leukopenia` is TIGHTENED
// (no `neutropenia`). DATA_ONLY concepts have NO pattern here → inert.
// Avoid risky abbreviations: no \bpt\b (matches "patient") — prothrombin_time is
// DATA_ONLY and uses no pattern anyway.
// ============================================================================
export const recognitionPatterns = {
  // existing measurements (verbatim)
  white_blood_cell_count: [/\bwbc\b/i, /\bwcc\b/i, /white\s+(blood\s+)?cell\s+count/i, /leukocyte count/i, /leucocyte count/i],
  platelet_count:         [/\bplt\b/i, /platelet/i],
  hemoglobin:             [/h[ae]moglobin/i, /\bhb\b/i, /\bhgb\b/i],
  // existing findings — leukopenia TIGHTENED (neutropenia removed)
  leukopenia:             [/leu[ck]openia/i, /low (white|wbc)/i],
  leukocytosis:           [/leu[ck]ocytosis/i, /high (white|wbc)/i],
  thrombocytopenia:       [/thrombocytopenia/i, /low platelet/i],
  anemia:                 [/an[ae]mia/i],

  // L-3 LIVE measurements
  ferritin:                   [/ferritin/i],
  mean_corpuscular_volume:    [/mean corpuscular volume/i, /\bmcv\b/i],
  vitamin_b12:                [/vitamin\s*b-?12/i, /\bb12\b/i, /cobalamin/i],
  folate:                     [/folate/i, /folic acid/i],
  reticulocyte_count:         [/reticulocyte/i, /retic count/i],
  lactate_dehydrogenase:      [/lactate dehydrogenase/i, /\bldh\b/i],
  haptoglobin:                [/haptoglobin/i],
  absolute_neutrophil_count:  [/absolute neutrophil/i, /\banc\b/i, /neutrophil count/i],
  lymphocyte_count:           [/lymphocyte count/i],

  // L-3 LIVE findings (subtypes ordered before generic anemia in index.js)
  iron_deficiency_anemia:     [/iron deficiency/i],
  macrocytic_anemia:          [/macrocytic/i],
  neutropenia:                [/neutropenia/i],
  thrombocytosis:             [/thrombocytosis/i, /high platelet/i],
  pancytopenia:               [/pancytopenia/i],
  polycythemia:               [/polycyth[ae]mia/i, /erythrocytosis/i],
  lymphocytosis:              [/lymphocytosis/i],
  lymphadenopathy:            [/lymphadenopathy/i, /enlarged lymph node/i, /swollen glands/i],
  splenomegaly:               [/splenomegaly/i, /enlarged spleen/i],

  // L-3 LIVE documents (LAB group, LAB_RESULT)
  blood_film:                 [/blood film/i, /peripheral (blood )?smear/i, /blood smear/i],
  iron_studies:               [/iron studies/i, /iron panel/i, /iron profile/i],
  coagulation_studies:        [/coagulation (studies|screen|profile)/i, /clotting screen/i, /coag screen/i],

  // L-3 LIVE red flags (binds the Pack red-flag label; matcher enforces positive_only)
  febrile_neutropenia:           [/febrile neutropenia/i, /neutropenic (fever|sepsis)/i, /fever with neutropenia/i],
  suspected_acute_leukemia:      [/acute leuk[ae]mia/i, /blasts? on (blood )?film/i, /circulating blasts/i, /suspected leuk[ae]mia/i],
  critically_low_platelet_count: [/critically low platelet/i, /severe thrombocytopenia/i, /platelet[s]?\s*(count\s*)?(<|below|under)\s*20\b/i],
};
